import { trackException } from '@/lib/telemetry'
import { FieldServiceProductType, ProductStatusReason } from '@/lib/types'
import { getMarginPercentage, getProductSearchtags, isCatalogPackage, isProduct } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { z } from 'zod'
import { dynamicsApi } from './client'
import { getMultipleRecords } from './helpers'
import {
  buildProductContainsFilter,
  buildStandardLaborProductFilter,
  sortStandardLaborProducts,
} from './product-search-helpers'
import { QUERY_KEYS } from './query-keys'
import { useQuoteQuery } from './quotes'
import { useSystemScopeTagsQuery, useSystemsQuery } from './rooms-and-systems'

// ==================== Product Fuzzy Search ====================

export type ProductSearchResult = {
  Id: string
  EntityName: string
  Attributes: {
    '@search.objecttypecode': number
    name: string
  }
  Highlights: Record<string, string[]>
  Score: number
}

type SearchQueryParsedResponse = {
  Value: ProductSearchResult[]
  Count: number
}

type ProductSearchOptions = {
  laborOnly?: boolean
  standardLaborOnly?: boolean
  otherChargeOnly?: boolean
}

type ProductSearchRow = {
  productid: string
  name?: string | null
  productnumber?: string | null
  dsy_partnumber?: string | null
  description?: string | null
}

const LABOR_PREFIX_FILTER =
  "(startswith(productnumber, 'LABOR:') or ((productnumber eq null or productnumber eq '') and startswith(name, 'LABOR:')))"

/**
 * Keeps catalog packages out of a product search.
 *
 * A package is an Other Charge product like any flat-price line, so the D-Tools
 * SI mapping search offers one unless it is filtered out — and an SI line mapped
 * to a package product reads as a package HEADER everywhere downstream, so its
 * dollars vanish from totals and work orders.
 *
 * Spelled out as false-or-null rather than `ne true` because the flag is null on
 * every product that predates the column. (Verified dev 2026-08-27: on
 * /products all three forms behave identically — 26 Other Charge rows, 25 under
 * each exclusion — but the explicit pair is immune to whichever null semantics
 * the endpoint applies.)
 */
export const EXCLUDE_CATALOG_PACKAGES_FILTER = '(dsy_ispackage eq false or dsy_ispackage eq null)'

/**
 * Drop catalog packages from relevance-search results, after the fact.
 *
 * The Dataverse search index for `product` is built from the Quick Find view's
 * columns and dsy_ispackage is not one of them, so naming it in the entity
 * filter is silently invalid — and an invalid entity filter makes the request
 * fall back to searching EVERY indexed table (verified dev 2026-08-27:
 * 602,684 hits, top results from msdynci_customerprofile). The exclusion
 * therefore runs here, over the ≤20 ids the search returned.
 */
async function excludeCatalogPackages(results: ProductSearchResult[]): Promise<ProductSearchResult[]> {
  if (!results.length) return results

  const idFilter = results.map((result) => `productid eq ${result.Id}`).join(' or ')
  const res = await dynamicsApi.get<{ value: { productid: string }[] }>(
    `/products?$select=productid&$filter=dsy_ispackage eq true and (${idFilter})`
  )
  const packages = res.data.value
  if (!packages.length) return results

  const packageIds = new Set(packages.map((row) => row.productid))
  return results.filter((result) => !packageIds.has(result.Id))
}

export async function searchProducts(search: string, options: ProductSearchOptions = {}): Promise<ProductSearchResult[]> {
  if (options.otherChargeOnly) {
    // Flat-price D-Tools SI labor maps to Other Charge products only.
    const searchFilter = buildProductContainsFilter(search)
    if (!searchFilter) return []

    const res = await dynamicsApi.get<{ value: ProductSearchRow[] }>(
      `/products?$select=productid,name,productnumber,dsy_partnumber,description&$filter=statecode eq 0 and statuscode eq 1 and msdyn_fieldserviceproducttype eq ${FieldServiceProductType.OtherCharge} and ${EXCLUDE_CATALOG_PACKAGES_FILTER} and (${searchFilter})&$orderby=name asc&$top=20`
    )

    return res.data.value.map((product, index) => ({
      Id: product.productid,
      EntityName: 'product',
      Attributes: {
        '@search.objecttypecode': 1024,
        name: product.name ?? product.productnumber ?? product.productid,
      },
      Highlights: {},
      Score: 20 - index,
    }))
  }

  if (options.laborOnly) {
    const searchFilter = options.standardLaborOnly
      ? buildStandardLaborProductFilter()
      : buildProductContainsFilter(search)
    if (!searchFilter) return []

    const res = await dynamicsApi.get<{ value: ProductSearchRow[] }>(
      `/products?$select=productid,name,productnumber,dsy_partnumber,description&$filter=statecode eq 0 and statuscode eq 1 and msdyn_fieldserviceproducttype eq ${FieldServiceProductType.Labor} and ${LABOR_PREFIX_FILTER} and (${searchFilter})&$orderby=name asc&$top=20`
    )

    const rows = options.standardLaborOnly ? sortStandardLaborProducts(res.data.value) : res.data.value
    return rows.map((product, index) => ({
      Id: product.productid,
      EntityName: 'product',
      Attributes: {
        '@search.objecttypecode': 1024,
        name: product.name ?? product.productnumber ?? product.productid,
      },
      Highlights: {},
      Score: 20 - index,
    }))
  }

  if (!search.trim()) return []

  // Escape Lucene special characters before appending fuzzy operator
  const escapedSearch = search.trim().replace(/[+\-&|!(){}[\]^"~*?:\\/]/g, '\\$&')
  // Indexed columns only — see excludeCatalogPackages for why the package
  // exclusion cannot join this filter.
  const filter = [
    'statecode eq 0',
    'statuscode eq 1',
  ].filter(Boolean).join(' and ')
  const res = await dynamicsApi.post<{ response: string }>('/searchquery', {
    search: `${escapedSearch}~`,
    entities: JSON.stringify([
      {
        name: 'product',
        selectColumns: ['productid'],
        searchColumns: ['productnumber', 'dsy_partnumber', 'description'],
        filter,
      },
    ]),
    options: JSON.stringify({ querytype: 'lucene' }),
    count: true,
    top: 20,
    skip: 0,
    orderby: JSON.stringify(['@search.score desc']),
  })

  const parsed: SearchQueryParsedResponse = JSON.parse(res.data.response)
  return await excludeCatalogPackages(parsed.Value ?? [])
}

export function useProductSearchQuery(search: string, enabled = true, options: ProductSearchOptions = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.productSearch(search, options),
    queryFn: () => searchProducts(search, options),
    enabled: enabled && (options.standardLaborOnly === true || search.trim().length >= 2),
    staleTime: 30_000,
  })
}

const catalogProductIdentitySchema = z.object({
  productid: z.string(),
  productnumber: z.string(),
  name: z.string().nullable(),
  statecode: z.number(),
  statuscode: z.number(),
})

export type CatalogProductIdentityData = z.infer<typeof catalogProductIdentitySchema>

export async function findProductByNumber(productNumber: string): Promise<CatalogProductIdentityData | null> {
  const products = await getMultipleRecords<unknown>(
    'products',
    `?$select=productid,productnumber,name,statecode,statuscode&$filter=productnumber eq '${encodeODataLiteral(productNumber)}'&$top=2`
  )
  const parsedProducts = products.map((product) => catalogProductIdentitySchema.parse(product))

  if (parsedProducts.length > 1) {
    throw new Error(`Multiple catalog products use product number ${productNumber}. Resolve duplicates before adding it.`)
  }

  return parsedProducts[0] ?? null
}

export function useExactProductQuery(productNumber: string, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.exactProduct(productNumber),
    queryFn: () => findProductByNumber(productNumber),
    enabled: enabled && productNumber.trim().length > 0,
    staleTime: 30_000,
    retry: 1,
  })
}

/** Strip Dynamics {crmhit} highlight markers, leaving plain text for scoring. */
export function stripHighlightMarkers(text: string): string {
  return text.replace(/\{\/?crmhit\}/g, '')
}

/** Escape HTML entities, then convert Dynamics {crmhit} markers to <mark> tags. */
export function sanitizeHighlight(text: string): string {
  return text
    .replace(/{crmhit}/g, '\0MARK_OPEN\0')
    .replace(/{\/crmhit}/g, '\0MARK_CLOSE\0')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/\0MARK_OPEN\0/g, '<mark>')
    .replace(/\0MARK_CLOSE\0/g, '</mark>')
}

// ==============================================================

// ==================== Product Image ====================

// Fetches the image binary through the authenticated Web API instead of the
// cookie-only /Image/download.aspx endpoint, so refreshing after the Dataverse
// session cookie expires doesn't 403 the request.
// Omitting `?size=full` is deliberate — Dataverse returns the 144x144 thumbnail
// by default, which is what product cards render. Never add `size=full` here.
export function useProductImageQuery(productId: string | null | undefined, hasImage: boolean) {
  return useQuery({
    queryKey: QUERY_KEYS.productImage(productId ?? ''),
    enabled: !!productId && hasImage,
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: false,
    queryFn: async () => {
      const res = await dynamicsApi.get<Blob>(`/products(${productId})/entityimage/$value`, {
        responseType: 'blob',
      })
      return await blobToDataUrl(res.data)
    },
  })
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}

// =======================================================

export const productSchema = z.object({
  productid: z.string({ message: 'Product ID is missing.' }),
  name: z.string({ message: 'Product name is missing.' }),
  msdyn_fieldserviceproducttype: z.nativeEnum(FieldServiceProductType, {
    message: 'Product field service type is invalid or missing.',
  }),
  entityimage: z.string().optional().nullable(),
  entityimage_url: z.string().optional().nullable(),
  dsy_defaultphase: z.number().nullable(),
  _defaultuomid_value: z.string().nullable(),
  dsy_partnumber: z.string().optional().nullable(),
  statuscode: z.nativeEnum(ProductStatusReason).optional().nullable(),
  statecode: z.number().optional().nullable(),
  dsy_weight: z.number().optional().nullable(),
  dsy_subcategory: z.string().optional().nullable(),
  dsy_vesapattern: z.string().optional().nullable(),
  // Package discriminator. Optional: fetch sites with explicit product $selects
  // predate this column and must keep parsing.
  dsy_ispackage: z.boolean().optional().nullable(),
})

export const priceListItemSchema = z.object({
  dsy_pricelistitemid: z.string({ message: 'Price list item ID is missing.' }),
  _dsy_product_value: z.string({ message: 'Product reference is missing on price list item.' }),
  _dsy_pricelist_value: z.string({ message: 'Price list reference is missing on price list item.' }),
  // Null in prod for some items (e.g. every LABOR:FINISH); normalized so callers keep string methods.
  dsy_description: z
    .string({ message: 'Description is missing on price list item.' })
    .nullable()
    .transform((v) => v ?? ''),
  dsy_unitcost: z.number().nullable(),
  dsy_unitprice: z.number().nullable(),
  dsy_taxable: z.boolean().nullable(),
  dsy_engineeringlabor: z.number({ message: 'Engineering labor rate is missing on price list item.' }),
  dsy_installationlabor: z.number({ message: 'Installation labor rate is missing on price list item.' }),
  dsy_roughinlabor: z.number({ message: 'Pre-wire labor rate is missing on price list item.' }),
  dsy_programminglabor: z.number({ message: 'Programming labor rate is missing on price list item.' }),
  dsy_projectmanagementlabor: z.number({ message: 'Project management labor rate is missing on price list item.' }),
  dsy_servicelabor: z.number().nullable(),
  dsy_product: productSchema,
  dsy_systemsearchtag: z.number().nullable(),
  dsy_scopesearchtag: z.number().nullable(),
  dsy_searchtag1text: z.string().nullable(),
  dsy_searchtag2text: z.string().nullable(),
  dsy_searchtag3text: z.string().nullable(),
  dsy_searchtag4text: z.string().nullable(),
  dsy_searchtag5text: z.string().nullable(),
  dsy_searchtag6text: z.string().nullable(),
  dsy_manufacturertext: z.string().nullable(),
})

export type PriceListItemData = z.infer<typeof priceListItemSchema>

const SEARCH_TOP = 100

// Escape single quotes per OData spec, then URL-encode reserved chars (`&`, `+`,
// `#`, etc.) so manufacturer names like "BANG & OLUFSEN" don't get parsed as
// query-string separators when interpolated into the request URL.
export function encodeODataLiteral(value: string): string {
  return encodeURIComponent(value.replace(/'/g, "''"))
}

export function useProductsQuery({
  tags,
  manufacturer,
  search,
  types,
  productIds,
  enabled = true,
  excludeUtilityProducts = false,
  includePackages = false,
}: {
  tags?: string[]
  manufacturer?: string | null
  search?: string
  types?: FieldServiceProductType[]
  productIds?: readonly string[]
  enabled?: boolean
  excludeUtilityProducts?: boolean
  /**
   * Offer catalog packages alongside products. Off by default so the searches
   * that feed another line's accessory or replacement list never return one —
   * a package header can only ever be a top-level row.
   */
  includePackages?: boolean
}) {
  const { data: quote } = useQuoteQuery()
  const { data: systems } = useSystemsQuery()
  const systemLabelToValue = useMemo(() => {
    return systems?.Options.reduce(
      (acc, system) => {
        const label = system.Label.UserLocalizedLabel.Label.toLowerCase()
        acc[label] = system.Value
        return acc
      },
      {} as Record<string, number>
    )
  }, [systems])

  const { data: systemScopeTags } = useSystemScopeTagsQuery()
  const systemScopeTagLabelToValue = useMemo(() => {
    return systemScopeTags?.Options.reduce(
      (acc, systemScopeTag) => {
        const label = systemScopeTag.Label.UserLocalizedLabel.Label.toLowerCase()
        acc[label] = systemScopeTag.Value
        return acc
      },
      {} as Record<string, number>
    )
  }, [systemScopeTags])

  const normalizedTags = useMemo(
    () => tags?.map((t) => t.trim().toLowerCase()).filter(Boolean) ?? [],
    [tags]
  )
  const normalizedManufacturer = manufacturer?.trim() || null
  const searchTerms = useMemo(
    () => search?.replace(/\s+/g, ' ').trim().toLowerCase().split(' ').filter(Boolean) ?? [],
    [search]
  )

  return useQuery({
    queryKey: QUERY_KEYS.products({ tags: normalizedTags, manufacturer: normalizedManufacturer, search, types, productIds }),
    enabled:
      !!quote && !!systems && !!systemScopeTags && !!systemLabelToValue && !!systemScopeTagLabelToValue && enabled,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
    queryFn: async ({ signal }): Promise<PriceListItemData[]> => {
      if (!quote || !systems || !systemScopeTags || !systemLabelToValue || !systemScopeTagLabelToValue || !enabled) {
        throw new Error('Query is not enabled.')
      }

      const select =
        '_dsy_product_value,_dsy_pricelist_value,dsy_unitcost,dsy_unitprice,dsy_taxable,dsy_description,dsy_engineeringlabor,dsy_installationlabor,dsy_roughinlabor,dsy_programminglabor,dsy_projectmanagementlabor,dsy_servicelabor,dsy_systemsearchtag,dsy_scopesearchtag,dsy_searchtag1text,dsy_searchtag2text,dsy_searchtag3text,dsy_searchtag4text,dsy_searchtag5text,dsy_searchtag6text,dsy_manufacturertext'

      const expand =
        'dsy_product($select=productid,name,msdyn_fieldserviceproducttype,dsy_defaultphase,entityimage_url,_defaultuomid_value,dsy_partnumber,statuscode,statecode,dsy_weight,dsy_subcategory,dsy_vesapattern,dsy_ispackage)'

      const listFilters: string[] = [
        `_dsy_pricelist_value eq '${encodeODataLiteral(quote._pricelevelid_value)}'`,
        'statecode eq 0',
      ]
      if (productIds?.length) {
        listFilters.push(`(${productIds.map((id) => `_dsy_product_value eq '${encodeODataLiteral(id)}'`).join(' or ')})`)
      }
      if (types) {
        listFilters.push(`(${types.map((type) => `dsy_product/msdyn_fieldserviceproducttype eq ${type}`).join(' or ')})`)
      }
      // The searches below leave product state out of the filter and have no
      // $orderby: either makes Dataverse walk the whole price list testing each
      // row (1.3-7s a search on dev vs 0.2-0.9s). Product state is checked on the
      // rows instead, below. Browse keeps both: it is fast unfiltered.
      const listFilter = listFilters.join(' and ')
      const baseFilter = `${listFilter} and dsy_product/statecode eq 0 and dsy_product/statuscode ne ${ProductStatusReason.Duplicate}`

      // Structured branch: option-set tag filter(s) AND/OR manufacturer prefix.
      // Uncapped — result set is bounded by the tag bucket size (dev max ~276 on
      // busiest scope tag) or manufacturer inventory.
      const structuredConditions: string[] = []
      const [systemTag, scopeTag] = normalizedTags
      if (scopeTag && systemTag) {
        const scopeValue = systemScopeTagLabelToValue[`${systemTag} ${scopeTag}`]
        if (scopeValue) structuredConditions.push(`dsy_scopesearchtag eq ${scopeValue}`)
      } else if (systemTag) {
        const systemValue = systemLabelToValue[systemTag]
        if (systemValue) structuredConditions.push(`dsy_systemsearchtag eq ${systemValue}`)
      }
      if (normalizedManufacturer) {
        structuredConditions.push(`startswith(dsy_name, '${encodeODataLiteral(normalizedManufacturer)}:')`)
      }
      const structuredFilter = structuredConditions.join(' and ')

      // Search branch: per-term OR across name/partnumber/description, AND across
      // terms. Capped to keep wide 1-term searches bounded (verified dev counts:
      // "tv" = 1676, "sony" = 259 — uncapped would flood).
      const searchFilter = searchTerms
        .map((term) => {
          const encoded = encodeODataLiteral(term)
          return `(contains(dsy_product/name, '${encoded}') or contains(dsy_product/dsy_partnumber, '${encoded}') or contains(dsy_description, '${encoded}'))`
        })
        .join(' and ')

      const structuredCombined = structuredFilter
        ? searchFilter
          ? `(${structuredFilter}) and (${searchFilter})`
          : structuredFilter
        : ''

      // The exact part number or name, read on its own: a broad word ("tv")
      // matches more items than the capped search read returns.
      const exactSearch = encodeODataLiteral(searchTerms.join(' '))
      const exactFilter = exactSearch
        ? `dsy_product/dsy_partnumber eq '${exactSearch}' or dsy_product/name eq '${exactSearch}' or endswith(dsy_product/name, ':${exactSearch}')`
        : ''

      const [exactResults, structuredResults, searchResults] = await Promise.all([
        exactFilter
          ? getMultipleRecords<PriceListItemData>(
              'dsy_pricelistitems',
              `?$select=${select}&$expand=${expand}&$filter=${listFilter} and (${exactFilter})&$top=${SEARCH_TOP}`,
              { signal }
            )
          : Promise.resolve<PriceListItemData[]>([]),
        structuredCombined
          ? getMultipleRecords<PriceListItemData>(
              'dsy_pricelistitems',
              `?$select=${select}&$expand=${expand}&$filter=${listFilter} and (${structuredCombined})`,
              { signal }
            )
          : Promise.resolve<PriceListItemData[]>([]),
        searchFilter
          ? getMultipleRecords<PriceListItemData>(
              'dsy_pricelistitems',
              `?$select=${select}&$expand=${expand}&$filter=${listFilter} and (${searchFilter})&$top=${SEARCH_TOP}`,
              { signal }
            )
          : Promise.resolve<PriceListItemData[]>([]),
      ])

      let products: PriceListItemData[]
      if (!structuredFilter && !searchFilter) {
        products = await getMultipleRecords<PriceListItemData>(
          'dsy_pricelistitems',
          `?$select=${select}&$expand=${expand}&$filter=${baseFilter}&$orderby=dsy_name asc&$top=${SEARCH_TOP}`,
          { signal }
        )
      } else {
        const seen = new Set<string>()
        products = exactResults.concat(structuredResults, searchResults).filter((product) => {
          if (seen.has(product.dsy_pricelistitemid)) return false
          seen.add(product.dsy_pricelistitemid)
          return true
        })
      }

      // Inactive and duplicate products never reach the picker; discontinued ones
      // do. Dropped before validation so a retired product's broken record
      // doesn't report as a schema failure.
      products = products.filter(
        (product) =>
          (product.dsy_product?.statecode ?? 0) === 0 &&
          product.dsy_product?.statuscode !== ProductStatusReason.Duplicate
      )

      const results = products.map((product) => priceListItemSchema.safeParse(product))
      const valid = results.filter((r): r is z.SafeParseSuccess<PriceListItemData> => r.success).map((r) => r.data)
      if (valid.length < products.length) {
        const failed = results
          .map((r, i) => {
            if (r.success) return null
            const raw = products[i] as Record<string, unknown>
            return {
              id: raw.dsy_pricelistitemid,
              errors: r.error.issues.map((issue) => `${issue.path.join('.')}: ${issue.message}`),
            }
          })
          .filter(Boolean)
        trackException(new Error(`${products.length - valid.length} product(s) failed schema validation and were excluded`), {
          context: 'products.useProductsQuery',
          totalRecords: String(products.length),
          droppedRecords: String(products.length - valid.length),
          failedItems: JSON.stringify(failed.slice(0, 10)),
        })
      }
      return valid
    },
    select: (data: PriceListItemData[]) => {
      const filtered = data.filter((product) => {
        // A catalog package is a sellable catalog entry, but isProduct() excludes
        // every package header — so it is admitted explicitly, and only where the
        // caller inserts top-level rows.
        const isSearchable =
          isProduct(product.dsy_product) || (includePackages && isCatalogPackage(product.dsy_product))
        if (excludeUtilityProducts && !isSearchable) return false
        if (product.dsy_product.name.includes('WRITE-IN PRODUCT')) return false

        return true
      })

      // Precompute scores to avoid recalculation during sort comparisons
      const matchers = buildPhraseMatchers(searchTerms)
      const scored = filtered.map((product) => ({
        product,
        discontinued: product.dsy_product.statuscode === ProductStatusReason.Discontinued,
        relevance: getRelevanceScore(product, searchTerms, matchers),
        margin: getMarginPercentage(product.dsy_unitprice, product.dsy_unitcost).toNumber(),
      }))

      scored.sort((a, b) => {
        if (a.discontinued !== b.discontinued) return a.discontinued ? 1 : -1
        if (a.relevance !== b.relevance) return b.relevance - a.relevance
        if (a.margin !== b.margin) return b.margin - a.margin
        return a.product.dsy_product.name.localeCompare(b.product.dsy_product.name, undefined, { numeric: true })
      })

      return scored.map((s) => s.product)
    },
  })
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export type PhraseMatcher = {
  subPhrase: string
  length: number
  regex: RegExp
}

/**
 * Precompile word-bounded regexes for every contiguous sub-sequence of
 * `searchTerms` of length >= 2, ordered longest-first (and left-to-right
 * within the same length). Build once per search and reuse across products
 * to avoid recompiling regexes in the hot scoring loop.
 *
 * `searchTerms` must already be lowercased.
 */
export function buildPhraseMatchers(searchTerms: string[]): PhraseMatcher[] {
  const matchers: PhraseMatcher[] = []
  for (let len = searchTerms.length; len >= 2; len--) {
    for (let start = 0; start <= searchTerms.length - len; start++) {
      const subPhrase = searchTerms.slice(start, start + len).join(' ')
      matchers.push({
        subPhrase,
        length: len,
        regex: new RegExp(`\\b${escapeRegex(subPhrase)}\\b`),
      })
    }
  }
  return matchers
}

/**
 * Score a product by the longest contiguous run of search terms that matches
 * word-bounded in `primary` or `secondary` text. Longer runs are stronger
 * signals that this is the specific product the user wants. Word boundaries
 * prevent "bravia 5" from matching "bravia 55" or "8" from matching "85".
 * Primary matches weigh more than secondary; a run anchoring the start of
 * primary gets an extra bump.
 *
 * `matchers` must be ordered longest-first (use `buildPhraseMatchers`). Both
 * `primary` and `secondary` must already be lowercased. Returns 0 when no
 * matcher fires (including single-term queries, which produce no matchers).
 */
export function scoreLongestTermRun(matchers: PhraseMatcher[], primary: string, secondary = ''): number {
  for (const m of matchers) {
    if (m.regex.test(primary)) {
      return m.length * 40 + (primary.startsWith(m.subPhrase) ? 50 : 0)
    }
    if (secondary && m.regex.test(secondary)) {
      return m.length * 25
    }
  }
  return 0
}

function getRelevanceScore(
  priceListItem: PriceListItemData,
  searchTerms: string[],
  matchers: PhraseMatcher[]
): number {
  let score = 0

  const search = searchTerms.join(' ')
  const productTags = getProductSearchtags(priceListItem).map((tag) => tag.toLowerCase())
  const manufacturerName = priceListItem.dsy_manufacturertext?.toLowerCase() ?? ''
  const productName = priceListItem.dsy_product.name.toLowerCase()
  const productPartNumber = priceListItem.dsy_product.dsy_partnumber?.toLowerCase() ?? ''
  const productDescription = priceListItem.dsy_description.toLowerCase()

  // Phrase-run bonus handles the common case where the display name is an SKU
  // and the real model info (e.g. "BRAVIA 8 II XR80M2") lives in the
  // description, with model numbers interrupting the full search phrase.
  const phraseBonus = scoreLongestTermRun(matchers, productName, `${productDescription} ${productPartNumber}`)
  if (phraseBonus > 0) {
    score += phraseBonus
  } else if (productName.includes(search)) {
    score += 10
  } else if (productPartNumber.includes(search)) {
    score += 7
  } else if (productDescription.includes(search)) {
    score += 5
  }

  for (const searchTerm of searchTerms) {
    const tagIndex = productTags.indexOf(searchTerm)
    if (tagIndex !== -1) {
      // Return early, must be greater than the best possible score otherwise (current: 52 + 8 possible tags)
      score += 60 - tagIndex
      continue
    }

    if (manufacturerName === searchTerm) {
      score += 10
    }

    if (productName === searchTerm) {
      score += 10
    } else if (productName.endsWith(searchTerm)) {
      score += 7
    } else if (productName.includes(searchTerm)) {
      score += 5
    }

    if (productPartNumber === searchTerm) {
      score += 10
    } else if (productPartNumber.startsWith(searchTerm) || productPartNumber.endsWith(searchTerm)) {
      score += 7
    } else if (productPartNumber.includes(searchTerm)) {
      score += 5
    }

    if (productDescription.includes(searchTerm)) {
      score += 2
    }
  }

  return score
}
